import { addMonths } from "@talentkontor/domain";
import { BLOCK_ORDER, buildView, compareFromDaily, totalsPeriod, type InsightsLang, type InsightsView, type MediaUrlResolver, type ShareResponse } from "../oeffentlich/insights-model";
import type { InstagramData } from "./data";

/**
 * Eigene Insights des Creators (0156) mit denselben Bausteinen wie die Ansicht der Marke (insights-model, InsightsAnsicht):
 * alle Blöcke, ohne Freigabe-Filter. Gleiche Regeln: „kein Wert“ bleibt leer, Reichweite nie aus Tageswerten, Zielgruppe nur
 * zusammengefasst, Beiträge nur als Link zu Instagram.
 */

/** Zeiträume der eigenen Ansicht (12 Monate und Gesamtverlauf zeigen zusätzlich die Monatswerte) */
export const OWN_PERIODS = ["7d", "30d", "90d", "12m", "all"] as const;

const AUDIENCE = ["age", "gender", "country", "city"] as const;

export function ownInsightsPayload(d: InstagramData, period: string, today: string): ShareResponse {
  const tp = totalsPeriod(period);
  const latest = d.totals.filter((t) => t.period === tp).sort((a, b) => b.computed_on.localeCompare(a.computed_on))[0] ?? null;
  const audience: Record<string, unknown> = {};
  let timeframe: string | null = null;
  for (const b of AUDIENCE) {
    const row = d.audience.filter((a) => a.breakdown === b).sort((x, y) => y.captured_on.localeCompare(x.captured_on))[0];
    if (row) audience[b] = row.data;
    if (row?.timeframe && !timeframe) timeframe = row.timeframe;
  }
  // 0340: Storys des Zeitraums (12 Monate und Gesamt: alle aufbewahrten, höchstens 90 Tage)
  const storyDays = tp === "7d" ? 7 : tp === "14d" ? 14 : tp === "30d" ? 30 : 90;
  const storyFrom = new Date(Date.parse(`${today}T00:00:00Z`) - (storyDays - 1) * 86_400_000).toISOString();
  const daily = (d.daily ?? []).map((x) => ({ day: x.day, metrics: x.metrics }));
  const cutoff = `${addMonths(today.slice(0, 7), -12)}-01`;
  const a = d.account;
  return {
    ok: true,
    blocks: [...BLOCK_ORDER],
    period,
    as_of: latest?.computed_on ?? null,
    talent_name: a?.name ?? null,
    profile: a ? { username: a.username, name: a.name, followers: a.followers_count, media_count: a.media_count, picture: a.profile_picture_path ?? null } : null,
    period_totals: latest ? latest.metrics : null,
    audience,
    monthly: period === "12m" || period === "all"
      ? d.monthly.filter((m) => period === "all" || m.month >= cutoff).sort((x, y) => x.month.localeCompare(y.month)).map((m) => ({ month: m.month, metrics: m.metrics }))
      : null,
    top_posts: [...d.media].sort((x, y) => (y.metrics.views ?? -1) - (x.metrics.views ?? -1)).slice(0, 6)
      .map((m) => ({ permalink: m.permalink, type: m.media_type, posted_at: m.posted_at, metrics: m.metrics, thumb: m.thumbnail_path ?? null })),
    daily,
    compare: compareFromDaily(daily, period, latest?.computed_on ?? null, BLOCK_ORDER),
    audience_timeframe: timeframe,
    stories: (d.stories ?? []).filter((s) => s.posted_at >= storyFrom).slice(0, 30)
      .map((s) => ({ posted_at: s.posted_at, type: s.media_type, metrics: s.metrics, measured_at: s.measured_at, measurements: s.measurements, few_viewers: s.few_viewers })),
  };
}

/** 0429: Bildpfade der eigenen Ansicht (Profilbild, Vorschaubilder der sechs stärksten Beiträge) für useIgMediaResolver */
export function ownMediaSource(d: InstagramData | null | undefined): Pick<ShareResponse, "blocks" | "profile" | "top_posts"> | null {
  const a = d?.account;
  if (!d || !a) return null;
  return {
    blocks: ["profile", "top_posts"],
    profile: { username: a.username, name: a.name, followers: a.followers_count, media_count: a.media_count, picture: a.profile_picture_path ?? null },
    top_posts: [...d.media].sort((x, y) => (y.metrics.views ?? -1) - (x.metrics.views ?? -1)).slice(0, 6)
      .map((m) => ({ permalink: m.permalink, type: m.media_type, posted_at: m.posted_at, metrics: m.metrics, thumb: m.thumbnail_path ?? null })),
  };
}

/** mediaUrl: signierte Adressen der eigenen Bilder (0429, useIgMediaResolver); ohne sie Platzhalter */
export function ownInsightsView(d: InstagramData, period: string, today: string, lang: InsightsLang, mediaUrl: MediaUrlResolver = () => null): InsightsView {
  return buildView(ownInsightsPayload(d, period, today), "", lang, mediaUrl);
}
